import { useState, useEffect } from "react";
import { useParams, useLocation } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { hasGroupAdmin } from "@/lib/pagePermissions";
import { formatCurrency } from "@/lib/utils";
import { ArrowLeft, Save, Loader2, CheckCircle2, AlertCircle, BookmarkPlus, Maximize2, Minimize2, Trash2 } from "lucide-react";
import { TemplateTrainer } from "@/components/template-trainer";
import { useDeleteReceipt } from "@/hooks/use-uploads";

export default function ReceiptReview() {
  const params = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  // Per-group admins (pageAdminPermissions: ["expenses"]) get the same
  // admin controls (e.g. "Train as Supplier Template") as global admins.
  const isAdmin = hasGroupAdmin(user, "expenses");
  const queryClient = useQueryClient();

  const { data: receipt, isLoading } = useQuery({
    queryKey: ["/api/receipts", params.id],
    queryFn: async () => {
      const res = await fetch(`/api/receipts/${params.id}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load receipt");
      return res.json();
    },
  });

  const [vendor, setVendor] = useState("");
  const [amount, setAmount] = useState("");
  const [receiptDate, setReceiptDate] = useState("");
  const [currency, setCurrency] = useState("GBP");
  const [vatAmount, setVatAmount] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");

  useEffect(() => {
    if (receipt) {
      setVendor(receipt.vendor || "");
      setAmount(receipt.amount?.toString() || "");
      setReceiptDate(receipt.receiptDate || "");
      setCurrency(receipt.currency || "GBP");
      setVatAmount(receipt.vatAmount?.toString() || "");
      setInvoiceNumber(receipt.invoiceNumber || "");
    }
  }, [receipt]);

  const updateMutation = useMutation({
    mutationFn: async (data: Record<string, any>) => {
      const res = await fetch(`/api/receipts/${params.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Update failed" }));
        throw new Error(err.error || "Failed to update receipt");
      }
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/receipts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/receipts", params.id] });
      if (data.matched) {
        toast({ title: "Receipt updated and matched to a statement entry!" });
      } else {
        toast({ title: "Receipt updated successfully" });
      }
    },
    onError: (error: Error) => {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
    },
  });

  const handleSave = () => {
    updateMutation.mutate({
      vendor: vendor.trim(),
      amount: amount ? parseFloat(amount) : null,
      receiptDate: receiptDate.trim(),
      currency,
      vatAmount: vatAmount ? parseFloat(vatAmount) : null,
      invoiceNumber: invoiceNumber.trim(),
    });
  };

  const deleteMutation = useDeleteReceipt();

  const handleDelete = () => {
    if (!receipt) return;
    const label = receipt.vendor || receipt.originalFilename || `receipt #${receipt.id}`;
    if (!confirm(`Delete ${label}? This will remove the file and any link to a statement entry. This cannot be undone.`)) return;
    deleteMutation.mutate(receipt.id, {
      onSuccess: () => navigate("/receipts"),
    });
  };

  const [trainerOpen, setTrainerOpen] = useState(false);
  const [detectedLabels, setDetectedLabels] = useState<any>(null);
  const [detecting, setDetecting] = useState(false);

  const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

  const handleOpenTrainer = async () => {
    if (!vendor.trim()) {
      toast({ title: "Please set a vendor name first", variant: "destructive" });
      return;
    }
    if (!receipt?.rawText) {
      toast({ title: "No OCR text available for this receipt", variant: "destructive" });
      return;
    }
    setDetecting(true);
    try {
      const res = await fetch(`${BASE}/api/supplier-templates/detect-labels`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          rawText: receipt.rawText,
          amount: amount || null,
          date: receiptDate || null,
          vatAmount: vatAmount || null,
          invoiceNumber: invoiceNumber || null,
          vendor: vendor || null,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setDetectedLabels(data);
      }
    } catch {
    } finally {
      setDetecting(false);
      setTrainerOpen(true);
    }
  };

  const previewUrl = `/api/receipts/${params.id}/preview`;
  const isImage = receipt?.originalFilename?.match(/\.(png|jpe?g|gif|webp)$/i);
  const [previewAvailable, setPreviewAvailable] = useState<boolean | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (!params.id) return;
    fetch(`/api/receipts/${params.id}/preview`, { credentials: "include" })
      .then((r) => setPreviewAvailable(r.ok))
      .catch(() => setPreviewAvailable(false));
  }, [params.id]);

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  if (!receipt) {
    return (
      <Layout>
        <div className="text-center py-24 text-slate-500">Receipt not found.</div>
      </Layout>
    );
  }

  const updatedReceipt = updateMutation.data;
  const currentMatched = updatedReceipt?.matched ?? receipt.matched;

  return (
    <Layout>
      <div className="mb-6 flex items-center gap-4">
        <button onClick={() => navigate("/receipts")} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-display font-bold text-slate-900">Review Receipt</h1>
          <p className="text-sm text-slate-500 mt-0.5">{receipt.originalFilename}</p>
        </div>
        <div>
          {currentMatched ? (
            <Badge variant="success" className="flex items-center gap-1.5 px-3 py-1.5 text-sm">
              <CheckCircle2 className="w-4 h-4" /> Matched
            </Badge>
          ) : (
            <Badge variant="warning" className="flex items-center gap-1.5 px-3 py-1.5 text-sm">
              <AlertCircle className="w-4 h-4" /> Unmatched
            </Badge>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" style={{ height: "calc(100vh - 180px)" }}>
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col">
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
            <span className="text-sm font-medium text-slate-600">File Preview</span>
            {previewAvailable && (
              <button
                onClick={() => setIsFullscreen(true)}
                className="p-1.5 rounded-lg hover:bg-slate-200 text-slate-500 hover:text-slate-700 transition-colors"
                title="View full screen"
              >
                <Maximize2 className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex-1 min-h-0 bg-slate-100">
            {previewAvailable === false ? (
              <div className="flex flex-col items-center justify-center h-full text-slate-400 px-6 text-center">
                <AlertCircle className="w-10 h-10 mb-3 text-slate-300" />
                <p className="font-medium text-slate-500">File preview unavailable</p>
                <p className="text-sm mt-1">The original file is no longer on disk. The extracted data is still available on the right.</p>
              </div>
            ) : previewAvailable === null ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-6 h-6 animate-spin text-slate-300" />
              </div>
            ) : isImage ? (
              <img
                src={previewUrl}
                alt={receipt.originalFilename}
                className="w-full h-full object-contain"
              />
            ) : (
              <iframe
                src={previewUrl}
                className="w-full h-full border-0"
                title="Receipt preview"
              />
            )}
          </div>
        </div>

        {isFullscreen && (
          <div className="fixed inset-0 z-50 bg-black/80 flex flex-col" onClick={() => setIsFullscreen(false)}>
            <div className="flex items-center justify-between px-4 py-3 bg-slate-900">
              <span className="text-sm font-medium text-white">{receipt.originalFilename}</span>
              <button
                onClick={() => setIsFullscreen(false)}
                className="p-2 rounded-lg hover:bg-slate-700 text-white transition-colors"
                title="Exit full screen"
              >
                <Minimize2 className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 min-h-0" onClick={(e) => e.stopPropagation()}>
              {isImage ? (
                <img
                  src={previewUrl}
                  alt={receipt.originalFilename}
                  className="w-full h-full object-contain"
                />
              ) : (
                <iframe
                  src={previewUrl}
                  className="w-full h-full border-0 bg-white"
                  title="Receipt preview"
                />
              )}
            </div>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col">
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 text-sm font-medium text-slate-600">
            Receipt Details
          </div>
          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Vendor</label>
                <input
                  type="text"
                  value={vendor}
                  onChange={(e) => setVendor(e.target.value)}
                  placeholder="e.g. Wickes, Tri-wall"
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Currency</label>
                  <select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white"
                  >
                    <option value="GBP">GBP (£)</option>
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="CAD">CAD</option>
                    <option value="AUD">AUD</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Receipt Date</label>
                <input
                  type="text"
                  value={receiptDate}
                  onChange={(e) => setReceiptDate(e.target.value)}
                  placeholder="e.g. 16/02/2026 or 16 Feb 2026"
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">VAT Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={vatAmount}
                    onChange={(e) => setVatAmount(e.target.value)}
                    placeholder="0.00"
                    className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Invoice Number</label>
                  <input
                    type="text"
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    placeholder="Optional"
                    className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>
              </div>

              {receipt.rawText && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Extracted Text (OCR)</label>
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600 max-h-40 overflow-y-auto font-mono whitespace-pre-wrap">
                    {receipt.rawText}
                  </div>
                </div>
              )}

              {isAdmin && <div className="border-t border-slate-200 pt-4">
                <button
                  type="button"
                  onClick={handleOpenTrainer}
                  disabled={detecting}
                  className="flex items-center gap-2 text-sm font-medium text-duffells-orange hover:text-duffells-orange/80 transition-colors disabled:opacity-50"
                >
                  {detecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookmarkPlus className="w-4 h-4" />}
                  {detecting ? "Analysing receipt…" : "Train as Supplier Template"}
                </button>
              </div>}
            </div>
          </div>

          <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center gap-3">
            <Button onClick={handleSave} disabled={updateMutation.isPending} className="flex-1">
              {updateMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" /> Save & Re-match
                </>
              )}
            </Button>
            <Button variant="outline" onClick={() => navigate("/receipts")}>
              Back
            </Button>
            <Button
              variant="outline"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
              title="Delete this receipt"
            >
              {deleteMutation.isPending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting...</>
              ) : (
                <><Trash2 className="w-4 h-4 mr-2" /> Delete</>
              )}
            </Button>
          </div>
        </div>
      </div>

      {receipt?.rawText && (
        <TemplateTrainer
          open={trainerOpen}
          onClose={() => setTrainerOpen(false)}
          rawText={receipt.rawText}
          previewUrl={previewUrl}
          isImage={!!isImage}
          vendor={vendor}
          currency={currency}
          receiptId={receipt.id}
          detectedLabels={detectedLabels}
        />
      )}
    </Layout>
  );
}
